import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, ArrowRight, Send, Layers, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { aiApi, segmentsApi } from "@/lib/leadsApi";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Avatar, ScoreBar, StatusBadge } from "@/components/find/atoms";

export default function AIProspect() {
    const { t } = useI18n();
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    async function run(query) {
        const finalQ = (query ?? q).trim();
        if (!finalQ) return;
        setLoading(true);
        try {
            const res = await aiApi.prospect(finalQ);
            setResult(res);
            refresh();
        } catch (e) {
            if (e?.response?.status === 402) toast.error(t.find.prospect.notEnough);
            else toast.error(e?.response?.data?.detail || "Prospecting failed");
        } finally {
            setLoading(false);
        }
    }

    async function saveAsSegment() {
        if (!result?.filter) return;
        const name = window.prompt("Segment name:", "AI segment");
        if (!name) return;
        // Convert AI filter into rule shape
        const conditions = [];
        const f = result.filter;
        if (f.industries?.length) conditions.push({ field: "industry", operator: "in", value: f.industries });
        if (f.sizes?.length) conditions.push({ field: "company_size", operator: "in", value: f.sizes });
        if (f.countries?.length) conditions.push({ field: "country", operator: "in", value: f.countries });
        if (f.seniorities?.length) conditions.push({ field: "seniority", operator: "in", value: f.seniorities });
        if (f.funding_stages?.length) conditions.push({ field: "funding_stage", operator: "in", value: f.funding_stages });
        if (f.min_score != null) conditions.push({ field: "score", operator: "gte", value: f.min_score });
        try {
            await segmentsApi.create({ name, rules: { combinator: "AND", conditions } });
            toast.success("Segment saved");
            navigate("/app/find/segments");
        } catch (_e) {
            toast.error("Save failed");
        }
    }

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{t.find.prospect.title}</h1>
                <p className="mt-1 text-sm text-zinc-500 max-w-3xl">{t.find.prospect.subtitle}</p>
            </div>

            {/* Input */}
            <div
                className="relative rounded-2xl p-6 border border-white/8 overflow-hidden mb-6"
                style={{ background: "radial-gradient(ellipse at 0% 0%, rgba(139,92,246,0.18), transparent 55%), radial-gradient(ellipse at 100% 100%, rgba(6,182,212,0.12), transparent 55%), #1A1A2E" }}
            >
                <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" />
                <div className="relative">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center shadow-[0_0_30px_-6px_rgba(139,92,246,0.7)] shrink-0">
                            <Wand2 size={16} className="text-white" />
                        </div>
                        <div className="flex-1">
                            <textarea
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={t.find.prospect.placeholder}
                                rows={3}
                                data-testid="prospect-input"
                                className="w-full bg-transparent outline-none text-lg text-white placeholder:text-zinc-500 resize-none font-display"
                            />
                            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                                <span className="text-xs text-zinc-500 font-mono">Powered by GPT-5.2 · 2 credits per query</span>
                                <button
                                    type="button"
                                    onClick={() => run()}
                                    disabled={loading || !q.trim()}
                                    data-testid="prospect-run"
                                    className="btn-cta text-white font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? <><Loader2 size={14} className="animate-spin" /> {t.find.prospect.running}</> : <><Sparkles size={14} /> {t.find.prospect.cta}</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Examples */}
            {!result && (
                <div className="mb-6">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{t.find.prospect.examples}</div>
                    <div className="flex flex-wrap gap-2">
                        {t.find.prospect.examplesList.map((ex, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => { setQ(ex); run(ex); }}
                                data-testid={`prospect-example-${i}`}
                                className="text-xs px-3 py-2 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/25 hover:bg-white/[0.08] text-zinc-300 hover:text-white transition"
                            >
                                {ex}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Results */}
            <AnimatePresence>
                {result && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        <div className="rounded-2xl p-5 bg-brand-surface border border-white/8">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{t.find.prospect.lastFilter}</div>
                            <pre className="text-xs font-mono text-zinc-200 bg-brand-void/60 rounded-lg p-3 border border-white/5 overflow-x-auto" data-testid="prospect-filter">
{JSON.stringify(result.filter, null, 2)}
                            </pre>
                            <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                                <div className="text-sm text-white" data-testid="prospect-matches">{t.find.prospect.matches(result.total_matches)}</div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={saveAsSegment}
                                        disabled={!result.filter || !Object.keys(result.filter).length}
                                        data-testid="prospect-save-segment"
                                        className="text-sm font-medium px-4 py-2 rounded-full border border-white/12 hover:border-white/25 hover:bg-white/5 text-white inline-flex items-center gap-2 disabled:opacity-40"
                                    >
                                        <Layers size={14} /> {t.find.prospect.createSegment}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/app/engage")}
                                        disabled={!result.matches.length}
                                        data-testid="prospect-push-sequence"
                                        className="btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50"
                                    >
                                        <Send size={14} /> {t.find.prospect.pushSequence}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {result.matches.length === 0 ? (
                            <div className="rounded-2xl p-8 text-center bg-brand-surface border border-white/8 text-sm text-zinc-500" data-testid="prospect-empty">
                                {t.find.prospect.empty}
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-white/8 bg-brand-surface overflow-hidden">
                                <div className="divide-y divide-white/8">
                                    {result.matches.slice(0, 30).map((l) => (
                                        <button
                                            key={l.lead_id}
                                            type="button"
                                            onClick={() => navigate(`/app/find/leads/${l.lead_id}`)}
                                            data-testid={`prospect-match-${l.lead_id}`}
                                            className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition"
                                        >
                                            <Avatar seed={l.avatar_seed} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-white font-medium truncate">{l.full_name}</span>
                                                    <span className="text-xs text-zinc-500">·</span>
                                                    <span className="text-xs text-zinc-400 truncate">{l.title}</span>
                                                </div>
                                                <div className="text-xs text-zinc-500 truncate">
                                                    {l.company_name} · {l.industry} · {l.city}, {l.country}
                                                </div>
                                            </div>
                                            <StatusBadge status={l.status} />
                                            <ScoreBar score={l.score} />
                                            <ArrowRight size={14} className="text-zinc-500" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
